import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from '../stylesheets/TodoItem.module.css';
import CheckmarkIconWhite from '../../assets/checkmark-icon.svg';
import CheckmarkIconPurple from '../../assets/checkmark-icon--purple.svg';
import TrashIcon from '../../assets/trash-icon.svg';
import PenIcon from '../../assets/pen-icon.svg';
import MoveIcon from '../../assets/move-icon.svg';
import { linkifyTaskText } from './linkifyTaskText.js';

const LINK_DISPLAY_HEAD_LENGTH = 36;
const LINK_DISPLAY_TAIL_LENGTH = 16;
const LINK_DISPLAY_MAX_LENGTH = LINK_DISPLAY_HEAD_LENGTH + LINK_DISPLAY_TAIL_LENGTH + 3;
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const RELATIVE_DAY_CUTOFF = 3;
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
});

function resizeTextarea(element) {
  if (!element) return;
  element.style.height = 'auto';
  element.style.height = `${element.scrollHeight}px`;
}

function truncateLinkLabel(url) {
  if (typeof url !== 'string') return '';
  if (url.length <= LINK_DISPLAY_MAX_LENGTH) return url;
  return `${url.slice(0, LINK_DISPLAY_HEAD_LENGTH)}...${url.slice(-LINK_DISPLAY_TAIL_LENGTH)}`;
}

function formatTodoDateValue(rawTimestamp, now) {
  if (typeof rawTimestamp !== 'string') return '';
  const parsedDate = new Date(rawTimestamp);
  if (Number.isNaN(parsedDate.getTime())) return '';

  const elapsedMs = now - parsedDate.getTime();

  if (elapsedMs < 0) {
    return SHORT_DATE_FORMATTER.format(parsedDate);
  }

  if (elapsedMs < MINUTE_MS) {
    return 'now';
  }

  if (elapsedMs < HOUR_MS) {
    const minutes = Math.floor(elapsedMs / MINUTE_MS);
    return `${minutes}m`;
  }

  if (elapsedMs < DAY_MS) {
    const hours = Math.max(1, Math.floor(elapsedMs / HOUR_MS));
    return `${hours}h`;
  }

  const days = Math.max(1, Math.floor(elapsedMs / DAY_MS));
  if (days < RELATIVE_DAY_CUTOFF) {
    return `${days}d`;
  }

  return SHORT_DATE_FORMATTER.format(parsedDate);
}

export default function TodoItem({
  todo,
  id,
  text,
  isCompleted = false,
  deleteTodo,
  toggleCompleted,
  editEntry,
  dragHandleProps = {},
  isDragging = false,
}) {
  const [inputValue, setInputValue] = useState(text);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(text);
  }, [text]);

  useEffect(() => {
    resizeTextarea(inputRef.current);
  }, [inputValue]);

  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    if (!input) return;

    input.focus();
    const length = input.value.length;
    input.setSelectionRange(length, length);
    resizeTextarea(input);
  }, [isEditing]);

  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => clearInterval(interval);
  }, []);

  const linkedTextSegments = useMemo(() => linkifyTaskText(inputValue), [inputValue]);
  const todoDateLabel = useMemo(() => {
    const timestamp = isCompleted ? todo.completedAt : todo.createdAt;
    const formattedValue = formatTodoDateValue(timestamp, now);
    if (!formattedValue) return '';
    return formattedValue;
  }, [isCompleted, todo.completedAt, todo.createdAt, now]);

  const onInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const onInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  const onInputBlur = () => {
    editEntry(id, inputValue);
    setIsEditing(false);
  };

  const startEditing = () => {
    if (isCompleted) return;
    setIsEditing(true);
  };

  const onDisplayClick = (event) => {
    if (event.target.closest('a')) return;
    startEditing();
  };

  return (
    <div className={cx(styles.todoDragContainer, isDragging && styles.noselect)}>
      <div
        className={cx(
          styles.dragIconContainer,
          isCompleted && styles.visibilityHidden,
          styles.nodrag,
        )}
      >
        {!isCompleted && (
          <button
            type="button"
            className={styles.dragHandleButton}
            aria-label="Reorder todo"
            {...dragHandleProps}
          >
            <img src={MoveIcon} className={cx(styles.moveIcon, styles.nodrag)} alt="" />
          </button>
        )}
      </div>

      <div className={cx(styles.todoContainer, isCompleted && styles.todoContainerCompleted)}>
        <button
          type="button"
          className={isCompleted ? styles.checkBoxCompleted : styles.checkBox}
          onClick={() => toggleCompleted(todo)}
          aria-label={isCompleted ? 'Mark as active' : 'Mark as completed'}
        >
          <img src={isCompleted ? CheckmarkIconWhite : CheckmarkIconPurple} alt="" />
        </button>

        <div className={styles.todoText}>
          {!isEditing || isCompleted ? (
            <div
              className={cx(styles.todoTextDisplay, isCompleted && styles.completedTodo)}
              onClick={onDisplayClick}
            >
              {linkedTextSegments.map((segment, index) => {
                if (segment.type === 'link') {
                  return (
                    <a
                      key={`segment-link-${id}-${index}`}
                      className={styles.todoTextLink}
                      href={segment.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={segment.value}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {truncateLinkLabel(segment.value)}
                    </a>
                  );
                }

                return (
                  <span key={`segment-text-${id}-${index}`}>{segment.value}</span>
                );
              })}
            </div>
          ) : (
            <textarea
              ref={inputRef}
              className={styles.inputField}
              value={inputValue}
              rows="1"
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              onBlur={onInputBlur}
            />
          )}
        </div>

        <div className={styles.todoMetaSlot}>
          {todoDateLabel && <div className={styles.todoMetaDate}>{todoDateLabel}</div>}
          <div className={styles.todoItemActions}>
            {!isCompleted && (
              <button
                type="button"
                className={styles.todoItemAction}
                onClick={startEditing}
                aria-label="Edit todo"
              >
                <img className={styles.nodrag} src={PenIcon} alt="" />
              </button>
            )}
            <button
              type="button"
              className={styles.todoItemAction}
              onClick={() => deleteTodo(id)}
              aria-label="Delete todo"
            >
              <img className={styles.nodrag} src={TrashIcon} alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

TodoItem.propTypes = {
  todo: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    done: PropTypes.bool,
    createdAt: PropTypes.string,
    completedAt: PropTypes.string,
  }).isRequired,
  id: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  isCompleted: PropTypes.bool,
  deleteTodo: PropTypes.func.isRequired,
  toggleCompleted: PropTypes.func.isRequired,
  editEntry: PropTypes.func.isRequired,
  dragHandleProps: PropTypes.object,
  isDragging: PropTypes.bool,
};
